import { useCallback, useMemo, useRef, useState } from 'react';

import CustomButton from '../../../common/CustomButton/CustomButton';
import CustomCheckbox from '../../../common/CustomCheckbox/CustomCheckbox';
import CustomForm from '../../../common/CustomForm/CustomForm';
import CustomInput from '../../../common/CustomInput/CustomInput';
import { Icon } from '../../../common/Icon/Icon';
import Spinner from '../../../common/Spinner/Spinner';
import { PASSWORD_REGEX } from '../../../core/constants';
import { useUserContext } from '../../../core/context/user.context';
import { buildSsoRedirectUrl, isEmpty, isValidEmail } from '../../../core/helpers';
import useFetch from '../../../core/hooks/useFetch';
import { useFormCheckbox } from '../../../core/hooks/useFormCheckbox';
import { useFormInput } from '../../../core/hooks/useFormInput';
import useModal from '../../../core/hooks/useModal';
import './LoginForm.css';

const BASE_URL = process.env.REACT_APP_API_URL;
type TStep = 'email' | 'password' | 'reset-password';

function LoginForm() {
  const resetPasswordSubmitBtnRef = useRef<HTMLButtonElement>(null);
  const [step, setStep] = useState<TStep>('email');
  const { doFetch: sendResetPasswordRequest } = useFetch(
    `${BASE_URL}/user/send-reset-password-link`,
  );
  const modalOptions = useMemo(() => ({ keepOpen: true }), []);
  const { modal, close } = useModal(modalOptions);

  const email = useFormInput('', (value) => isValidEmail(value));
  const password = useFormInput('', (value) => !isEmpty(value) && PASSWORD_REGEX.test(value));
  const remember = useFormCheckbox(false);

  const { login, loginLoading, checkIfSSO, ssoCheckLoading } = useUserContext();

  const showPassword = () => {
    setStep('password');
    setTimeout(() => {
      password.ref?.current?.focus();
    }, 0);
  };

  const showResetPassword = () => {
    setStep('reset-password');
    setTimeout(() => {
      resetPasswordSubmitBtnRef?.current?.focus();
    }, 0);
  };

  const backToEmail = () => {
    setStep('email');
    setTimeout(() => {
      email.ref.current?.focus();
    }, 0);
    close();
  };

  const handleSSOCheck = useCallback(async () => {
    const sso = await checkIfSSO(email);
    if (sso?.force_sso && sso?.sso_url) {
      window.location.href = buildSsoRedirectUrl(sso.sso_url);
    } else {
      showPassword();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [email]);

  const handleLogin = useCallback(() => {
    login(email, password, remember);
  }, [email, login, password, remember]);

  const handleResetPassword = useCallback(() => {
    sendResetPasswordRequest({
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        email: email.value,
      }),
    });
  }, [email.value, sendResetPasswordRequest]);

  return (
    <>
      {step !== 'email' ? null : (
        <CustomForm className="video-banner__form" inputs={[email]} onSubmit={handleSSOCheck}>
          <CustomInput
            name="email"
            input={email}
            className="video-banner__input"
            type="email"
            id="login-email"
            label="Email"
            invalidMessage="Please fill in a valid email address"
            emptyMessage="Please fill in your email"
          />
          <button className="video-banner__submit" type="submit">
            {ssoCheckLoading ? <Spinner /> : <Icon.Arrow />}
            <span className="sr-only">Login</span>
          </button>
        </CustomForm>
      )}
      {step === 'email'
        ? null
        : modal({
            side: 'center',
            skipFocus: true,
            children: (
              <>
                <button className="modal__close" onClick={backToEmail} type="button">
                  <Icon.Plus className="icon-large" />
                  <span className="sr-only">Close modal</span>
                </button>
                {step !== 'reset-password' ? null : (
                  <CustomForm
                    className="video-banner__modal-form"
                    inputs={[email]}
                    onSubmit={handleResetPassword}
                  >
                    <>
                      <h2 className="video-banner__modal-title video-banner__modal-title--reset-password h4">
                        Enter your email and we will send you a password reset link
                      </h2>
                      <CustomInput
                        name="email"
                        input={email}
                        className="video-banner__input video-banner__input--fixed-label"
                        type="email"
                        id="login-email"
                        label="Email address"
                      />

                      <CustomButton
                        className="video-banner__modal-cta btn btn--green"
                        type="submit"
                        loading={loginLoading}
                        ref={resetPasswordSubmitBtnRef}
                      >
                        Request a new password
                      </CustomButton>
                      <CustomButton
                        className="video-banner__modal-email link"
                        type="button"
                        onClick={backToEmail}
                      >
                        Back to login
                      </CustomButton>
                    </>
                  </CustomForm>
                )}
                {step !== 'password' ? null : (
                  <CustomForm
                    className="video-banner__modal-form"
                    inputs={[email, password]}
                    onSubmit={handleLogin}
                  >
                    <>
                      <h2 className="video-banner__modal-title h2">Log in</h2>
                      {!email.value ? null : (
                        <CustomButton
                          className="video-banner__modal-email"
                          type="button"
                          onClick={backToEmail}
                        >
                          {email.value}
                        </CustomButton>
                      )}
                      <CustomButton
                        className="video-banner__modal-reset"
                        type="button"
                        onClick={showResetPassword}
                      >
                        Need a password?
                      </CustomButton>
                      <CustomInput
                        id="login-password"
                        type="password"
                        name="password"
                        input={password}
                        label="Password"
                        className="video-banner__input"
                        emptyMessage="Please fill in your password"
                        invalidMessage="Please fill in a valid password, minimum eight characters, one uppercase letter, one lowercase letter, one digit and one special character."
                      />
                      <div className="video-banner__input-footer">
                        <CustomCheckbox
                          id="remember"
                          name="remember"
                          label="Remember me"
                          input={remember}
                        />
                      </div>
                    </>
                    <CustomButton
                      className="video-banner__modal-cta btn btn--green"
                      type="submit"
                      loading={loginLoading}
                    >
                      Log in
                    </CustomButton>
                    <CustomButton
                      className="video-banner__modal-email link"
                      type="button"
                      onClick={backToEmail}
                    >
                      Change email
                    </CustomButton>
                  </CustomForm>
                )}
              </>
            ),
          })}
    </>
  );
}

export default LoginForm;
